import { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Table, Spinner, Badge, Modal, InputGroup } from 'react-bootstrap';
import { useApi } from '../../../contexts/ApiProvider';

export default function HobbyInventory({ hobbyId }) {
    const api = useApi();
    const [items, setItems] = useState([]);
    const [blueprint, setBlueprint] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [editId, setEditId] = useState(null);

    const [formData, setFormData] = useState({
        name: '', category: 'Tools', qty: 1, status: 'Active', attributes: {}
    });

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [hobbyId]);

    const fetchData = async () => {
        setIsLoading(true);
        const [itemsRes, fieldsRes] = await Promise.all([
            api.get(`/hobbies/${hobbyId}/items?per_page=100`),
            api.get(`/hobbies/${hobbyId}/fields`)
        ]);

        if (itemsRes.ok) setItems(itemsRes.body.items || []);
        if (fieldsRes.ok) setBlueprint(fieldsRes.body.items.filter(f => f.entity_type === 'Item') || []);
        setIsLoading(false);
    };

    const handleDynamicChange = (fieldName, value) => {
        setFormData(prev => ({
            ...prev,
            attributes: { ...prev.attributes, [fieldName]: value }
        }));
    };

    const openCreateModal = () => {
        setEditId(null);
        setFormData({ name: '', category: 'Tools', qty: 1, status: 'Active', attributes: {} });
        setShowModal(true);
    };

    const openEditModal = (item) => {
        setEditId(item.id);
        setFormData({
            name: item.name,
            category: item.category,
            qty: item.qty,
            status: item.status,
            attributes: item.attributes || {}
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const res = editId
            ? await api.put(`/hobby-items/${editId}`, formData)
            : await api.post(`/hobbies/${hobbyId}/items`, formData);

        if (res.ok) {
            setShowModal(false);
            fetchData();
        } else {
            alert("Failed to save item.");
        }
    };

    const handleDelete = async (itemId) => {
        if (!window.confirm("Remove this item?")) return;
        const res = await api.delete(`/hobby-items/${itemId}`);
        if (res.ok) fetchData();
    };

    // --- NEW: Helper to format attributes in the table ---
    const renderAttributeValue = (fieldName, rawValue) => {
        if (!rawValue && rawValue !== 0 && typeof rawValue !== 'boolean') return '--';

        const fieldDef = blueprint.find(f => f.field_name === fieldName);

        if (fieldDef && fieldDef.data_type === 'number') {
            const unit = (fieldDef.options && fieldDef.options.length > 0) ? ` ${fieldDef.options[0]}` : '';
            return `${rawValue}${unit}`;
        }

        if (fieldDef && fieldDef.data_type === 'datetime') {
            return new Date(rawValue).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
        }

        if (fieldDef && fieldDef.data_type === 'boolean') {
            return rawValue ? 'Yes' : 'No';
        }

        if (fieldDef && fieldDef.data_type === 'item_reference') {
            const linkedItem = items.find(i => i.id.toString() === rawValue.toString());
            return linkedItem ? linkedItem.name : 'Unknown Item';
        }

        if (fieldDef && fieldDef.data_type === 'multi_item_reference') {
            if (!Array.isArray(rawValue)) return rawValue.toString();
            const itemNames = rawValue.map(id => {
                const linkedItem = items.find(i => i.id.toString() === id.toString());
                return linkedItem ? linkedItem.name : 'Unknown';
            });
            return itemNames.join(', ');
        }

        return rawValue.toString();
    };

    if (isLoading) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;

    return (
        <div className="fade-in">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="fw-bold mb-0">Inventory & Tools</h5>
                <Button variant="primary" size="sm" className="fw-bold rounded-pill px-3" onClick={openCreateModal}>
                    <i className="bi bi-plus-lg me-1"></i>Add Item
                </Button>
            </div>

            {items.length === 0 ? (
                <div className="text-center py-5 text-muted bg-light rounded-4 border">
                    <p className="mb-0">No items in your inventory yet.</p>
                </div>
            ) : (
                <Card className="border-0 shadow-sm rounded-4">
                    <Card.Body className="p-0">
                        <Table hover responsive className="align-middle mb-0">
                            <thead className="bg-light">
                                <tr className="text-muted small">
                                    <th className="ps-4">Item Name</th>
                                    <th>Category</th>
                                    <th>Status</th>
                                    <th>Qty</th>
                                    <th>Custom Attributes</th>
                                    <th className="text-end pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(item => (
                                    <tr key={item.id}>
                                        <td className="fw-bold ps-4">{item.name}</td>
                                        <td><Badge bg="secondary">{item.category}</Badge></td>
                                        <td>
                                            <Badge bg={
                                                item.status === 'Active' ? 'success' :
                                                item.status === 'In Use' ? 'primary' :
                                                item.status === 'Damaged/Trashed' ? 'danger' : 'secondary'
                                            }>
                                                {item.status}
                                            </Badge>
                                        </td>
                                        <td>{item.qty}</td>
                                        <td className="small text-muted">
                                            {Object.entries(item.attributes || {}).map(([key, val]) => (
                                                <div key={key}><strong>{key}:</strong> {renderAttributeValue(key, val)}</div>
                                            ))}
                                        </td>
                                        <td className="text-end pe-4 text-nowrap">
                                            <Button variant="link" className="text-primary p-0 me-3" onClick={() => openEditModal(item)}>
                                                <i className="bi bi-pencil-square hover-scale"></i>
                                            </Button>
                                            <Button variant="link" className="text-danger p-0" onClick={() => handleDelete(item.id)}>
                                                <i className="bi bi-trash3 hover-scale"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} centered fullscreen="md-down">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">{editId ? 'Edit Inventory Item' : 'Add Inventory Item'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold text-muted mb-1">Item Name</Form.Label>
                            <Form.Control type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required />
                        </Form.Group>
                        <Row className="mb-3 g-2">
                            <Col md={4}>
                                <Form.Label className="small fw-bold text-muted mb-1">Category</Form.Label>
                                <Form.Control type="text" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} />
                            </Col>
                            <Col md={4}>
                                <Form.Label className="small fw-bold text-muted mb-1">Status</Form.Label>
                                <Form.Select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})}>
                                    <option value="Active">Active / Available</option>
                                    <option value="In Use">In Use</option>
                                    <option value="Archived">Archived</option>
                                    <option value="Damaged/Trashed">Damaged/Trashed</option>
                                </Form.Select>
                            </Col>
                            <Col md={4}>
                                <Form.Label className="small fw-bold text-muted mb-1">Quantity</Form.Label>
                                <Form.Control type="number" step="0.5" value={formData.qty} onChange={e => setFormData({...formData, qty: e.target.value})} />
                            </Col>
                        </Row>

                        {blueprint.length > 0 && <h6 className="fw-bold mt-4 mb-3 border-bottom pb-2">Custom Fields</h6>}

                        {blueprint.filter(field => {
                            if (!field.target_category) return true;

                            const currentCategory = (formData.category || '').trim().toLowerCase();
                            // Split the rule by commas, clean up spaces, and check if our category is in the list!
                            const targetCategories = field.target_category.split(',').map(c => c.trim().toLowerCase());

                            return targetCategories.includes(currentCategory);
                        }).map(field => (
                            <Form.Group className="mb-3 fade-in" key={field.id}>
                                <Form.Label className="small fw-bold text-primary mb-1">{field.field_name}</Form.Label>

                                {field.data_type === 'text' && (
                                    <Form.Control type="text" value={formData.attributes[field.field_name] || ''} onChange={e => handleDynamicChange(field.field_name, e.target.value)} />
                                )}

                                {field.data_type === 'number' && (
                                    <InputGroup>
                                        <Form.Control type="number" step="0.1" value={formData.attributes[field.field_name] || ''} onChange={e => handleDynamicChange(field.field_name, e.target.value)} />
                                        {field.options && field.options.length > 0 && (
                                            <InputGroup.Text className="bg-light text-muted small">{field.options[0]}</InputGroup.Text>
                                        )}
                                    </InputGroup>
                                )}

                                {field.data_type === 'datetime' && (
                                    <Form.Control type="datetime-local" value={formData.attributes[field.field_name] || ''} onChange={e => handleDynamicChange(field.field_name, e.target.value)} />
                                )}

                                {field.data_type === 'dropdown' && (
                                    <Form.Select value={formData.attributes[field.field_name] || ''} onChange={e => handleDynamicChange(field.field_name, e.target.value)}>
                                        <option value="">Select...</option>
                                        {field.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                    </Form.Select>
                                )}

                                {field.data_type === 'boolean' && (
                                    <Form.Check type="switch" checked={formData.attributes[field.field_name] || false} onChange={e => handleDynamicChange(field.field_name, e.target.checked)} />
                                )}

                                {field.data_type === 'item_reference' && (
                                    <Form.Select value={formData.attributes[field.field_name] || ''} onChange={e => handleDynamicChange(field.field_name, e.target.value)}>
                                        <option value="">Select an item...</option>
                                        {items.map(item => (
                                            <option key={item.id} value={item.id}>
                                                {item.name} {item.category ? `(${item.category})` : ''}
                                            </option>
                                        ))}
                                    </Form.Select>
                                )}

                                {field.data_type === 'multi_item_reference' && (() => {
                                    let availableItems = items;
                                    if (field.options && field.options.length > 0) {
                                        availableItems = items.filter(item => item.category && field.options.includes(item.category.trim()));
                                    }
                                    return (
                                        <>
                                            <Form.Select multiple value={formData.attributes[field.field_name] || []} onChange={e => {
                                                const selectedOpts = Array.from(e.target.selectedOptions, option => option.value);
                                                handleDynamicChange(field.field_name, selectedOpts);
                                            }}>
                                                {availableItems.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                                            </Form.Select>
                                            <Form.Text className="text-muted small">Hold Ctrl/Cmd to select multiple.</Form.Text>
                                        </>
                                    );
                                })()}
                            </Form.Group>
                        ))}

                        <Button variant="primary" type="submit" className="w-100 fw-bold rounded-pill mt-3">Save Item</Button>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
}
